import * as _ from 'lodash'
import { cloneElement, Component } from 'react';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { Location } from 'history';
import type { RouteComponentProps } from 'react-router';

import { isDependencyCacheValid } from '../../../helpers/revisions';
import NavigationStore from '../../../redux/navigation';
import ProjectGroupModule from '../../../redux/project_grouping';
import { toFiniteNumber } from '../../../utils';
import type { IssueScanScope } from '../../../../typings/api';
import type { NavigationState } from '../../../redux/navigation/types';
import type { IAsyncStatus, ProjectGroup, ProjectGroupRelease, ProjectGroupReleaseScan } from '../../../types';

import ProjectGroupNav from './ProjectGroupNav';
import ReleaseGroupSubnav from './subnav';

type Props = {
  location: Location<{ releaseScanId?: string }>;
};

type StateProps = {
  projectGroup: ProjectGroup;
  selectedRelease: Partial<ProjectGroupRelease>;
  selectedScanId?: number;
  scansStatus: IAsyncStatus;
  release: ProjectGroupRelease;
  releaseScans: ProjectGroupReleaseScan[];
  getPollRelease: {projectGroupId: string, releaseId: number};
  releaseSummary: {
    /* eslint-disable @typescript-eslint/naming-convention -- These are coming from the backend */
    license_count: number;
    dependency_count: number;
    project_count: number;
    /* eslint-enable @typescript-eslint/naming-convention */
    releasesCount: number;
  };
  releaseSummaryStatus: IAsyncStatus;
};

type DispatchProps = {
  fetchProjectGroup(id: number, scanId?: number): Promise<void>;
  fetchRelease(projectGroupId: number, id: number): Promise<void>;
  fetchReleaseSummary(projectGroupId: number, id: number): Promise<void>;
  pollReleaseStart(projectGroupId: number, id: number): Promise<void>;
  pollReleaseStop(): Promise<void>;
  setNavState: (newState: Partial<NavigationState>) => void;
};

type ProjectGroupIndexParams = {
  projectGroupId: number;
};

type ProjectGroupContainerProps = Props & StateProps & RouteComponentProps<ProjectGroupIndexParams, {}> & DispatchProps;

export class ProjectGroupContainer extends Component<ProjectGroupContainerProps> {
  async componentDidMount() {
    const scanId = toFiniteNumber(this.props.location.query.releaseScanId);
    await this.props.fetchProjectGroup(this.props.params.projectGroupId, scanId);
    if (this.props.selectedRelease?.id) {
      await this.props.fetchRelease(this.props.params.projectGroupId, this.props.selectedRelease.id);
      if (!this.props.scansStatus.loading) {
        await this.props.fetchReleaseSummary(this.props.params.projectGroupId, this.props.selectedRelease.id);
      }
    }
    this.onUpdateNavigation();
  }

  async componentDidUpdate(prevProps: Readonly<ProjectGroupContainerProps>) {
    if (!_.isEqual(this.props.projectGroup?.releases, prevProps.projectGroup?.releases)) {
      this.onUpdateNavigation();
    }

    // poll for if dependency cache is invalid
    if (this.props.release?.id && this.props.getPollRelease?.releaseId !== this.props.release.id && !this.allDependencyCacheValid(this.props)) {
      this.props.pollReleaseStart(this.props.params.projectGroupId, this.props.release.id);
    } else if (this.props.release?.id && prevProps.release?.id && this.allDependencyCacheValid(this.props) && !this.allDependencyCacheValid(prevProps)) {
      this.props.pollReleaseStop();
    }

    if (!this.props.selectedRelease?.id || !prevProps.selectedRelease?.id || this.props.scansStatus.loading) {
      return;
    }

    if (prevProps.selectedRelease.id !== this.props.selectedRelease.id) {
      await this.props.fetchRelease(this.props.params.projectGroupId, this.props.selectedRelease.id);
      await this.props.fetchReleaseSummary(this.props.params.projectGroupId, this.props.selectedRelease.id);
    } else if (prevProps.releaseScans?.length !== this.props.releaseScans.length) {
      await this.props.fetchReleaseSummary(this.props.params.projectGroupId, this.props.selectedRelease.id);
    } else if (prevProps.selectedScanId && prevProps.selectedScanId !== this.props.selectedScanId) {
      await this.props.fetchReleaseSummary(this.props.params.projectGroupId, this.props.selectedRelease.id);
    }
  }

  componentWillUnmount() {
    if (this.props.release?.id && !this.allDependencyCacheValid(this.props)) {
      this.props.pollReleaseStop();
    }
  }

  allDependencyCacheValid(props) {
    return props.release?.projects.every(
      project =>
        isDependencyCacheValid(project.revision)
    );
  }

  onUpdateNavigation() {
    const {
      projectGroup,
      params: { projectGroupId },
      location,
    } = this.props;
    this.props.setNavState({
      expand: 'project-expanded',
      subnav: ProjectGroupNav,
      navProps: {
        projectGroup,
        projectGroupId,
        location,
        //  This ensures that the call to action is shown on the project group page
        shouldUseCallToAction: true,
      },
    });
  }

  getScanScope = (): IssueScanScope => ({
    type: 'releaseGroup',
    groupId: this.props.projectGroup.id,
    releaseId: this.props.selectedRelease.id,
    scanId: this.props.selectedScanId,
  });

  render() {
    const { projectGroup, selectedRelease, releaseSummary, releaseSummaryStatus } = this.props;
    // don't render the child components until the projectGroup and selectedRelease exists
    if (!projectGroup || !selectedRelease) {
      return null;
    }
    const {
      license_count: licenseCount,
      dependency_count: dependencyCount,
      project_count: projectCount,
    } = releaseSummary;
    const { loading: isLoadingSummary } = releaseSummaryStatus;
    const currentReleaseId = selectedRelease?.id ?? projectGroup.releases[0]?.id;
    return (
      <div>
        <Helmet title={`${this.props.projectGroup.title} - Release Groups`} />
        <ReleaseGroupSubnav
          projectGroupId={projectGroup.id}
          projectCount={projectCount}
          dependencyCount={dependencyCount}
          licenseCount={licenseCount}
          releasesCount={projectGroup.releases?.length ?? 0}
          isLoadingSummary={isLoadingSummary}
          currentReleaseId={currentReleaseId}
        />
        {cloneElement(this.props.children, {
          projectGroup: this.props.projectGroup,
          projectGroupRelease: this.props.selectedRelease,
          scanScope: this.getScanScope(),
          fetchProjectGroup: this.props.fetchProjectGroup,
        })}
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  projectGroup: ProjectGroupModule.selectors.getCurrentProjectGroup,
  release: ProjectGroupModule.selectors.getRelease,
  selectedRelease: ProjectGroupModule.selectors.getSelectedRelease,
  selectedScanId: ProjectGroupModule.selectors.getSelectedScanId,
  releaseScans: ProjectGroupModule.selectors.getReleaseScans,
  scansStatus: ProjectGroupModule.selectors.getReleaseScanStatus,
  getPollRelease: ProjectGroupModule.selectors.getPollRelease,
  releaseSummary: ProjectGroupModule.selectors.getReleaseSummary,
  releaseSummaryStatus: ProjectGroupModule.selectors.getReleaseSummaryStatus,
});

const mapDispatchToProps: DispatchProps = {
  fetchProjectGroup: ProjectGroupModule.actions.fetchOne,
  fetchRelease: ProjectGroupModule.actions.fetchRelease,
  fetchReleaseSummary: ProjectGroupModule.actions.fetchReleaseSummary,
  pollReleaseStart: ProjectGroupModule.actions.pollReleaseStart,
  pollReleaseStop: ProjectGroupModule.actions.pollReleaseStop,
  setNavState: NavigationStore.actions.setNavState,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProjectGroupContainer);
